import React from "react";
import { Check } from "lucide-react";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { CtaBand } from "@/pages/Home";
import { PRODUCTS } from "@/data/site";

const SERVER_IMG =
  "https://images.unsplash.com/photo-1680992046626-418f7e910589?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxzZXJ2ZXIlMjByb29tJTIwbGlnaHRzJTIwZGFya3xlbnwwfHx8fDE3ODMzMDM0MzF8MA&ixlib=rb-4.1.0&q=85";

const SUPPORT = [
  { k: "Technology", v: "Our liquidity bridge and analytics platform are built and maintained in-house by experienced trading and technology professionals." },
  { k: "Connectivity", v: "Connect via API for orders, market data and post-trade, integrating cleanly with your existing platforms and workflows." },
  { k: "Support", v: "A dedicated dealing and support team on hand through market hours to help resolve issues swiftly and keep your desk trading." },
];

export default function Technology() {
  return (
    <div data-testid="technology-page">
      <PageHero
        overline="Technology"
        title="Liquidity, connectivity and analytics — in one platform."
        subtitle="ET Prime pairs a multi-asset liquidity and execution bridge with real-time risk analytics, equipping brokers and funds with the tools to manage flow, exposure and profitability with confidence."
      />

      {/* PRODUCTS */}
      <section className="py-24 md:py-32 relative z-10" data-testid="tech-products">
        <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-8">
          {PRODUCTS.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.08}>
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 border border-white/10 bg-[#11151C]/40 p-10 md:p-14 hover:border-[#C8A45A]/40 transition-colors" data-testid={`product-${p.name.toLowerCase()}`}>
                <div className="lg:col-span-5">
                  <span className="font-mono text-xs uppercase tracking-widest text-[#737882]">{p.kind}</span>
                  <h2 className="font-display text-4xl md:text-5xl font-light text-white mt-4">
                    {p.name}
                  </h2>
                  <p className="gold-text mt-4 text-lg">{p.tagline}</p>
                  <p className="text-[#A1A5AB] mt-6 leading-relaxed">{p.body}</p>
                </div>
                <div className="lg:col-span-7 lg:border-l lg:border-white/10 lg:pl-10 flex items-center">
                  <ul className="space-y-5 w-full">
                    {p.features.map((f) => (
                      <li key={f} className="flex gap-4 text-[#A1A5AB]">
                        <Check className="gold-text shrink-0 mt-1" size={18} />
                        <span className="leading-relaxed">{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PARTNERSHIP BAND */}
      <section className="relative z-10 overflow-hidden border-y border-white/10" data-testid="tech-partnership">
        <div className="absolute inset-0">
          <img src={SERVER_IMG} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-[#0A0D12]/88" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 md:px-12 py-24 md:py-32 text-center">
          <Reveal>
            <span className="overline">Built on partnership</span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white mt-6 leading-tight">
              Technology is only half of it. The relationship is the rest.
            </h2>
            <p className="mt-8 text-lg text-[#A1A5AB] leading-relaxed max-w-3xl mx-auto">
              Our tools give you control and clarity. Our team gives you a wholesale counterparty that
              understands your book and stands behind it — combining hard-won trading knowledge with
              versatile, self-service technology.
            </p>
          </Reveal>
        </div>
      </section>

      {/* TECHNOLOGY & SUPPORT */}
      <section className="py-24 md:py-32 relative z-10" data-testid="tech-support">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <span className="overline">Technology & support</span>
            <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight text-white mt-5">
              A solid base, versatile tools.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10 mt-16">
            {SUPPORT.map((row, i) => (
              <Reveal key={row.k} delay={i * 0.08} className="bg-[#0A0D12] p-10 hover:bg-[#11151C] transition-colors">
                <h3 className="font-display text-xl text-white">{row.k}</h3>
                <p className="text-sm text-[#A1A5AB] mt-4 leading-relaxed">{row.v}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
